import React, { useContext, useEffect, useRef, useState } from 'react'

import { DEFAULT_ICON_SIZE } from '../hooks/useFloorChart'
import { useDeviceConnections } from './useDeviceConnections'
import { useFloorData } from './useFloorData'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { ApolloQueryResult } from '@apollo/client'
import { useDisclosure } from '@chakra-ui/react'

import { DragEventI } from '@/components/ui'
import { useTailgateMixpanelEvents } from '@/features/tailgate-grid'
import { FacilityDevicesByFloorsQuery } from '@/graphql/generated/operations'
import {
  DeviceType,
  Exact,
  UpdateTailgateEnabledInput,
} from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { AppState } from '@/redux/store'
import {
  openDeviceDrawer,
  saveFloorPlanIconSize,
  selectEditMode,
  selectFacilityDrawerOpen,
  selectFloorPlanIconSize,
  setDevice,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, mixpanel, trackEvent } from '@/utils/analytics'
import { SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

import { PopoverElementKind } from '../floor-plan-popover/components/FloorPlanPopover'
import {
  AssociateDeviceI,
  ConnectionI,
  DeviceHoverItemI,
  DeviceI,
  FloorI,
  Point,
  TailgateHoverItemI,
} from '../types/types'
import { getTailgateConnectionElementId } from '../utils/chart'

export type RefetchDevicesFunction = (
  variables?: Partial<
    Exact<{
      floorId: string
      types?: DeviceType | DeviceType[]
    }>
  >
) => Promise<ApolloQueryResult<FacilityDevicesByFloorsQuery>>

type FloorPlanContextType = {
  floorId: string
  selectedDevice: DeviceI
  devices: DeviceI[]
  checkpointDevices: DeviceI[]
  imageUrl: string
  isFloorDataLoading: boolean
  isRemoveCheckPointOpen: boolean
  isAssociateDeviceOpen: boolean
  isTailgateGridOpen: boolean
  isTailgateHovering: boolean
  tailgateHoverItem: TailgateHoverItemI
  isDeviceHovering: boolean
  deviceHoverItem: DeviceHoverItemI
  associateDeviceValues: AssociateDeviceI
  shouldShowAssociatedDevices: boolean
  highlightedDeviceIds: string[]
  isConnectionsLoading: boolean
  isEnableTailgateLoading: boolean
  floorPlanIconSize: number
  nameLikeFilter: string
  onIconResize: (newSize: number) => void
  setNameLikeFilter: (filter: string) => void
  connections: ConnectionI[]
  connectDevices: (
    camera: DeviceI,
    door: DeviceI,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  disconnectDevices: (
    cameraId: string,
    doorId: string,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  enableTailgate: (
    input: UpdateTailgateEnabledInput,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  getConnection: (cameraId: string, doorId: string) => ConnectionI
  setHighlightedDeviceIds: React.Dispatch<React.SetStateAction<string[]>>
  setShouldShowAssociatedDevices: React.Dispatch<React.SetStateAction<boolean>>
  onDeviceClicked: (device: DeviceI) => void
  onFloorPlanContainerClick: () => void
  onMoveCheckPointClicked: (deviceId: string) => void
  onRemoveCheckpoint: (device: DeviceI) => void
  onDeviceDragged: (device: DeviceI, isPositionValid: boolean) => void
  onDeviceDragEnd: (device: DeviceI, isPositionValid: boolean) => void
  onTileDragStart: (event: DragEventI) => void
  handleDeviceDropped: (position: Point, isValidPosition: boolean) => boolean
  onDeviceHover: (
    d: DeviceI & ConnectionI,
    id: string,
    kind: PopoverElementKind
  ) => void
  onDeviceHoverOut: () => void
  onConnectionButtonClicked: (con: ConnectionI) => void
  onRemoveCheckPointClose: () => void
  onAssociateDeviceClose: () => void
  onTailgateGridOpen: () => void
  onTailgateGridClose: () => void
  refetchDevices: RefetchDevicesFunction
  trackTailgateEvent: (name: string) => void
}

export const FloorPlanContext = React.createContext<FloorPlanContextType>(null)

interface FloorPlanProviderIProps {
  floorId: string
  floors: FloorI[]
  facilityId: string
  children: React.ReactNode
}

export const FloorPlanProvider = ({
  floorId,
  floors,
  facilityId,
  children,
}: FloorPlanProviderIProps) => {
  const dispatch = useDispatch()
  const me = useSelector(selectMe)
  const isEditMode = useSelector(selectEditMode)
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  const isGuardTour =
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'
  const deviceTypes = isGuardTour
    ? [DeviceType.Checkpoint]
    : [DeviceType.Alarm, DeviceType.Camera, DeviceType.Door]

  const [selectedDevice, setSelectedDevice] = useState(null)
  const [draggedData, setDraggedData] = useState(null)
  const [isTailgateHovering, setIsTailgateHovering] = useState(false)
  const [tailgateHoverItem, setTailgateHoverItem] =
    useState<TailgateHoverItemI>(null)
  const [isDeviceHovering, setIsDeviceHovering] = useState(false)
  const [deviceHoverItem, setDeviceHoverItem] = useState<DeviceHoverItemI>(null)

  const [associateDeviceValues, setAssociateDeviceValues] =
    useState<AssociateDeviceI>(null)
  const [highlightedDeviceIds, setHighlightedDeviceIds] =
    useState<string[]>(null)
  const [shouldShowAssociatedDevices, setShouldShowAssociatedDevices] =
    useState(true)
  const [nameLikeFilter, setNameLikeFilter] = useState<string>('')
  const [debouncedNameLikeFilter] = useDebounce(
    nameLikeFilter,
    SEARCH_DEBOUNCE_TIME
  )

  // Track device search events
  useEffect(() => {
    if (debouncedNameLikeFilter) {
      trackEvent('Floor Plan - Device Search', {
        search_term: debouncedNameLikeFilter,
        show_associated_devices: shouldShowAssociatedDevices,
        facility_id: facilityId,
        floor_id: floorId,
      })
    }
  }, [
    debouncedNameLikeFilter,
    shouldShowAssociatedDevices,
    facilityId,
    floorId,
  ])

  const iconSizeFromState: number = useSelector((state: AppState) =>
    selectFloorPlanIconSize(state, floorId)
  )

  const [floorPlanIconSize, setFloorPlanIconSize] = useState(
    iconSizeFromState || DEFAULT_ICON_SIZE
  )

  useEffect(() => {
    setFloorPlanIconSize(iconSizeFromState || DEFAULT_ICON_SIZE)
  }, [iconSizeFromState])

  const editModeRef = useRef(isEditMode)
  editModeRef.current = isEditMode

  const {
    isOpen: isRemoveCheckPointOpen,
    onOpen: onRemoveCheckPointOpen,
    onClose: onRemoveCheckPointClose,
  } = useDisclosure()

  const {
    isOpen: isAssociateDeviceOpen,
    onOpen: onAssociateDeviceOpen,
    onClose: onAssociateDeviceClose,
  } = useDisclosure()

  const {
    isOpen: isTailgateGridOpen,
    onOpen: onTailgateGridOpen,
    onClose: onTailgateGridClose,
  } = useDisclosure()

  const {
    devices,
    checkpointDevices,
    imageUrl,
    loading: isFloorDataLoading,
    onDeviceClicked: onFCDecivceClicked,
    onFloorPlanContainerClick,
    onMoveCheckPointClicked,
    onDeviceUpdated,
    onDeviceReleased,
    onDeviceDropped,
    refetchDevices,
  } = useFloorData(
    floorId,
    floors,
    facilityId,
    isEditMode,
    deviceTypes,
    debouncedNameLikeFilter,
    shouldShowAssociatedDevices
  )

  const {
    isLoading: isConnectionsLoading,
    isEnableTailgateLoading,
    connections,
    connectDevices,
    disconnectDevices,
    enableTailgate,
    getConnection,
  } = useDeviceConnections(devices)

  const { trackTailgateEvent } = useTailgateMixpanelEvents({
    facilityId,
    floorId,
    cameraId: associateDeviceValues?.cameraId,
    doorId: associateDeviceValues?.doorId,
  })

  const getDevice = (id: string): DeviceI => {
    return devices.find((d) => d.id === id)
  }

  const onDeviceClicked = (data: DeviceI) => {
    if (!editModeRef.current) {
      handleOpenDeviceDrawer(data.id, data.deviceType)
      return
    }
    if (data.deviceType === DeviceType.Checkpoint) {
      setHighlightedDeviceIds([data.id])
    }
    setHighlightedDeviceIds([data.id])
    setSelectedDevice(data)
    onFCDecivceClicked(data.id)
  }

  const handleOpenDeviceDrawer = (deviceId: string, deviceType: DeviceType) => {
    dispatch(setDevice({ id: deviceId, type: deviceType }))
    dispatch(openDeviceDrawer())
  }

  const onRemoveCheckpoint = (c: DeviceI) => {
    setSelectedDevice(c)
    onRemoveCheckPointOpen()
    mixpanel.track('Button Press: Map Checkpoint - Remove from floor', {
      checkpoint_id: c.checkpoint.id,
      facility_id: c.checkpoint.facility.id,
      floor_id: c.checkpoint.floor.id,
      role: me.roleName,
    })
  }

  const onDeviceDragged = (data: DeviceI, isPositionValid: boolean) => {
    handleDeviceUpdated(data, isPositionValid)
  }

  const onDeviceDragEnd = (data: DeviceI, isPositionValid: boolean) => {
    handleDeviceReleased(data, isPositionValid)
  }

  const handleDeviceUpdated = (data: DeviceI, isPositionValid: boolean) => {
    if (!editModeRef.current) {
      return
    }
    onDeviceUpdated(data, isPositionValid)
  }

  const handleDeviceReleased = (data: DeviceI, isPositionValid: boolean) => {
    if (!editModeRef.current) return
    handleDeviceUpdated(data, isPositionValid)

    onDeviceReleased(data, isPositionValid)
  }

  // Called when drag from panel
  const onTileDragStart = (event: DragEventI) => {
    if (!editModeRef.current) return
    setDraggedData(event)
  }

  // Called when dropped from panel
  const handleDeviceDropped = (position: Point, isValidPosition: boolean) => {
    if (!editModeRef.current || !draggedData) return
    if (isValidPosition) {
      onDeviceDropped(draggedData.data.id, position)
    }
    setDraggedData(null)
    return false
  }

  const onDeviceHover = (
    data: DeviceI & ConnectionI,
    elementId: string,
    kind: PopoverElementKind
  ) => {
    switch (kind) {
      case 'tailgate':
        setTailgateHoverItem({
          elementId: getTailgateConnectionElementId(data),
          connection: data,
          cameraName: getDevice(data?.cameraId).deviceName,
          doorName: getDevice(data?.deviceId).deviceName,
        })
        setIsTailgateHovering(true)
        break

      default:
        setDeviceHoverItem({
          elementId,
          deviceId: data.id,
          deviceName: data.deviceName,
          deviceType: data.deviceType,
        })
        setIsDeviceHovering(true)
        break
    }
  }

  const onIconResize = (newSize: number) => {
    setFloorPlanIconSize(newSize)

    // TODO - Look at deboucing this? If somebody is on a lower powered device
    //  and drags the slider up and down a ton it might cause issues saving
    //  each little step
    dispatch(
      saveFloorPlanIconSize({
        floorId: floorId,
        iconSize: newSize,
      })
    )
  }

  const onDeviceHoverOut = () => {
    setIsTailgateHovering(false)
    setIsDeviceHovering(false)
  }

  const onConnectionButtonClicked = (con: ConnectionI) => {
    const camera = getDevice(con.cameraId)
    const device = getDevice(con.deviceId)
    mixpanel.track(`${BUTTON_PRESS} Device Association`, {
      facility_id: facilityId,
      floor_id: floorId,
      camera_id: con.cameraId,
      door_id: con.deviceId,
      role: me.roleName,
    })

    if (editModeRef.current) {
      setAssociateDeviceValues({
        cameraId: con.cameraId,
        doorId: con.deviceId,
        cameraName: camera.deviceName,
        doorName: device.deviceName,
        deviceType: con.deviceType,
      })
      onAssociateDeviceOpen()
    }
  }

  return (
    <FloorPlanContext.Provider
      value={{
        floorId,
        devices,
        checkpointDevices,
        selectedDevice,
        imageUrl,
        isFloorDataLoading,
        isRemoveCheckPointOpen,
        isTailgateHovering,
        tailgateHoverItem,
        isDeviceHovering,
        deviceHoverItem,
        associateDeviceValues,
        shouldShowAssociatedDevices,
        isAssociateDeviceOpen,
        isTailgateGridOpen,
        highlightedDeviceIds,
        isConnectionsLoading,
        isEnableTailgateLoading,
        connections,
        floorPlanIconSize,
        nameLikeFilter,
        onIconResize,
        setNameLikeFilter,
        connectDevices,
        disconnectDevices,
        enableTailgate,
        getConnection,
        setHighlightedDeviceIds,
        setShouldShowAssociatedDevices,
        onDeviceClicked,
        onFloorPlanContainerClick,
        onMoveCheckPointClicked,
        onRemoveCheckpoint,
        onRemoveCheckPointClose,
        onDeviceDragged,
        onDeviceDragEnd,
        onTileDragStart,
        handleDeviceDropped,
        onDeviceHover,
        onDeviceHoverOut,
        onConnectionButtonClicked,
        onAssociateDeviceClose,
        onTailgateGridOpen,
        onTailgateGridClose,
        refetchDevices,
        trackTailgateEvent,
      }}
    >
      {children}
    </FloorPlanContext.Provider>
  )
}

export const useFloorPlan = () => {
  return useContext(FloorPlanContext)
}
